// =============================================================================
// CADASTRO UNIFICADO DO CLIENTE - permissão "CRIAÇÃO DE OS SIMPLIFICADA".
//
// Fluxo pedido pelo cliente (analise_do_cliente.md), na ordem dele:
//   Nome → Endereço → Documentos → Equipamentos → Ambientes → Cronograma →
//   MTTR/MTBF → Gerar e imprimir
//   "Tudo centralizado no cadastro do cliente, sem precisar ficar entrando em
//    várias telas ou fazendo vários cadastros separados."
//
// O PMOC nasce AQUI. Não existe mais tela "Novo PMOC" separada: o plano é do
// cliente, e os equipamentos e ambientes que ele puxa são os desta mesma ficha —
// mandar o usuário para outra tela só para gerar era ida e volta sem motivo.
//
// A ORDEM DO DOCUMENTO TEM UM NÓ, e ele está resolvido aqui: Ambientes vem por
// último, mas o equipamento precisa de um ambiente. Em vez de trocar a ordem, o
// passo Equipamentos permite criar o ambiente na hora ("+ novo ambiente"), e o
// passo Ambientes é onde se completa área, ocupantes e atividade de cada um.
//
// O cliente é GRAVADO no fim do primeiro passo: documentos, equipamentos e
// ambientes são registros filhos e precisam de um cliente_id. Daí em diante cada
// passo salva sozinho, então sair no meio não perde o que já foi feito.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getToken, urlImagem } from '../lib/api';
import { API_BASE } from '../config';
import { useAuth } from '../lib/auth';
import Passos, { Passo } from '../components/Passos';

interface ClienteFicha {
  id: number;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  whatsapp: string | null;
  status: string;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  endereco_linha: string;
  // Unidade provisionada pelo cadastro (estruturaLocalDoCliente). E a chave que o
  // PMOC usa para filtrar MTTR/MTBF e programacoes deste cliente.
  unidade_id?: number | null;
}

// Linha editavel do cronograma. Vem pre-preenchida com as atividades sugeridas
// pelo backend - o "so iria ajustar" do documento, nao digitar de zero.
interface LinhaCronograma {
  marcada: boolean;
  nome: string;
  periodicidade_dias: string;
  norma_referencia: string;
}

// Equipamento que estava em outro plano e foi movido para este ao gerar.
interface EquipamentoTransferido {
  ativo_id: number;
  nome: string;
  de_plano_id: number;
  de_plano_nome: string | null;
}

// pmoc/plano_do_cliente.php (GET) - o que sera puxado, e as sugestoes de atividade.
interface PreviaPmoc {
  equipamentos: Array<{ id: number; nome: string; em_outro_plano?: boolean; outro_plano_nome?: string | null }>;
  equipamentos_disponiveis: number;
  planos_existentes: Array<{ id: number; nome: string; status: string }>;
  atividades_sugeridas: Array<{ nome: string; periodicidade_dias: number; norma_referencia?: string | null }>;
}

// pmoc/dashboard.php -> mttr_mtbf. Indicadores CALCULADOS a partir das falhas
// registradas; nao sao campos que se preenchem.
interface MttrMtbf {
  eventos_falha: number;
  // Preventiva / gerada pelo PMOC: fica registrada mas NAO conta como falha.
  paradas_planejadas?: number;
  mttr_tecnico_hhmm: string | null;
  mttr_operacional_hhmm: string | null;
  mtbf_hhmm: string | null;
  disponibilidade_percentual: number | null;
  top_mttr?: Array<{ ativo_id: number; ativo_nome: string; falhas: number; mttr_operacional_minutos: number | null }>;
  top_menor_mtbf?: Array<{ ativo_id: number; ativo_nome: string; falhas: number; mtbf_minutos: number | null }>;
}

interface Ambiente {
  id: number;
  nome: string;
  area_m2: number | null;
  ocupantes_max: number | null;
  atividade_ambiente: string | null;
  observacao: string | null;
  status: string;
  equipamentos: number;
}

interface Equipamento {
  id: number;
  nome: string;
  patrimonio: string | null;
  marca: string | null;
  modelo: string | null;
  numero_serie: string | null;
  status: string;
  capacidade_btu: number | null;
  cliente_ambiente_id: number | null;
  ambiente_nome: string | null;
  categoria_id: number | null;
  categoria_nome: string | null;
}

interface Categoria {
  id: number;
  nome: string;
  tipo: string;
  status: string;
}

interface AtivoExistente {
  id: number;
  nome: string;
  patrimonio: string | null;
  marca: string | null;
  modelo: string | null;
  numero_serie: string | null;
  capacidade_btu?: number | null;
  cliente_ambiente_id?: number | null;
  categoria_id?: number | null;
}

interface AmbienteModelo {
  id: number;
  nome: string;
  area_m2: number | null;
  ocupantes_max: number | null;
  atividade_ambiente: string | null;
  observacao: string | null;
  cliente_nome?: string | null;
}

interface Documento {
  id: number;
  tipo: string;
  titulo: string;
  arquivo_url: string;
  data_emissao: string | null;
  data_validade: string | null;
  observacao: string | null;
}

interface Ficha {
  cliente: ClienteFicha;
  ambientes: Ambiente[];
  equipamentos: Equipamento[];
  documentos: Documento[];
  tipos_documento: string[];
}

const ROTULO_TIPO_DOC: Record<string, string> = {
  contrato: 'Contrato',
  art_trt: 'ART / TRT',
  laudo: 'Laudo',
  certificado: 'Certificado',
  proposta: 'Proposta',
  projeto: 'Projeto',
  manual: 'Manual',
  outro: 'Outro',
};

const IDENTIFICACAO_VAZIA = { nome: '', documento: '', email: '', telefone: '', whatsapp: '' };
const ENDERECO_VAZIO = { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };
const EQUIPAMENTO_VAZIO = { nome: '', capacidade_btu: '', cliente_ambiente_id: '', categoria_id: '', marca: '', modelo: '', patrimonio: '', numero_serie: '' };
const AMBIENTE_VAZIO = { nome: '', area_m2: '', ocupantes_max: '', atividade_ambiente: '', observacao: '' };
const DOCUMENTO_VAZIO = { titulo: '', tipo: 'contrato', data_emissao: '', data_validade: '' };
const apenasDigitos = (v: string): string => v.replace(/\D/g, '');

interface ViaCepResposta {
  erro?: boolean;
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
}

const btuTexto = (v: number | null): string =>
  v === null ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' BTU';
const dataTexto = (v: string | null): string => (v ? v.split('-').reverse().join('/') : '—');

interface ClienteUnificadoProps {
  clienteIdInicial?: number | null;
  onVoltar?: () => void;
}

export default function ClienteUnificado({ clienteIdInicial, onVoltar }: ClienteUnificadoProps = {}) {
  const nav = useNavigate();
  const { id: idParam } = useParams();
  const idRota = idParam ? Number(idParam) : null;
  const idInicial = clienteIdInicial !== undefined ? clienteIdInicial : idRota;
  const [clienteId, setClienteId] = useState<number | null>(idInicial);

  const [identificacao, setIdentificacao] = useState(IDENTIFICACAO_VAZIA);
  const [endereco, setEndereco] = useState(ENDERECO_VAZIO);
  const { isSemPmoc, user } = useAuth();
  // Mesmo rotulo que a empresa deu ao campo numero_serie na tela de Equipamentos.
  const rotuloSerie = user?.rotulo_numero_serie || 'Número de série';
  const [unidadeCliente, setUnidadeCliente] = useState<number | null>(null);
  // ---- PMOC gerado a partir desta ficha ----
  const [previaPmoc, setPreviaPmoc] = useState<PreviaPmoc | null>(null);
  const [planoId, setPlanoId] = useState<number | null>(null);
  const [nomePlano, setNomePlano] = useState('');
  const [dataInicioPmoc, setDataInicioPmoc] = useState(new Date().toISOString().slice(0, 10));
  const [dataValidadePmoc, setDataValidadePmoc] = useState('');
  const [cronograma, setCronograma] = useState<LinhaCronograma[]>([]);
  const [resumoPuxado, setResumoPuxado] = useState<{ ambientes: number; ativos: number } | null>(null);
  const [resumoCronograma, setResumoCronograma] = useState<{ atividades: number; programacoes: number } | null>(null);
  const [transferidos, setTransferidos] = useState<EquipamentoTransferido[]>([]);
  const [indicadores, setIndicadores] = useState<MttrMtbf | null>(null);
  const [carregandoInd, setCarregandoInd] = useState(false);
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [ativosDisponiveis, setAtivosDisponiveis] = useState<AtivoExistente[]>([]);
  const [ambientesDisponiveis, setAmbientesDisponiveis] = useState<AmbienteModelo[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<string[]>(Object.keys(ROTULO_TIPO_DOC));

  const [novoEquip, setNovoEquip] = useState(EQUIPAMENTO_VAZIO);
  const [novoAmbiente, setNovoAmbiente] = useState(AMBIENTE_VAZIO);
  const [equipamentoExistenteId, setEquipamentoExistenteId] = useState('');
  const [ambienteModeloId, setAmbienteModeloId] = useState('');
  const [ambienteInline, setAmbienteInline] = useState('');
  const [tipoChamadoInline, setTipoChamadoInline] = useState('');
  const [buscaEquipamento, setBuscaEquipamento] = useState('');
  const [buscaAmbiente, setBuscaAmbiente] = useState('');
  const [novoDoc, setNovoDoc] = useState(DOCUMENTO_VAZIO);
  const [arquivoDoc, setArquivoDoc] = useState<File | null>(null);
  const [editandoAmbiente, setEditandoAmbiente] = useState<number | null>(null);

  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const ultimoCepConsultado = useRef('');

  const aplicarFicha = useCallback((f: Ficha) => {
    setClienteId(f.cliente.id);
    setUnidadeCliente(f.cliente.unidade_id ?? null);
    setIdentificacao({
      nome: f.cliente.nome || '',
      documento: f.cliente.documento || '',
      email: f.cliente.email || '',
      telefone: f.cliente.telefone || '',
      whatsapp: f.cliente.whatsapp || '',
    });
    setEndereco({
      cep: f.cliente.cep || '',
      logradouro: f.cliente.logradouro || '',
      numero: f.cliente.numero || '',
      complemento: f.cliente.complemento || '',
      bairro: f.cliente.bairro || '',
      cidade: f.cliente.cidade || '',
      uf: f.cliente.uf || '',
    });
    setAmbientes(f.ambientes);
    setEquipamentos(f.equipamentos);
    setDocumentos(f.documentos);
    if (f.tipos_documento?.length) setTiposDocumento(f.tipos_documento);
  }, []);

  const carregarFicha = useCallback(async (cid: number) => {
    try {
      aplicarFicha(await api<Ficha>('/cadastros/cliente_ficha.php?cliente_id=' + cid));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }, [aplicarFicha]);

  const carregarCategorias = useCallback(async () => {
    try {
      setCategorias(await api<Categoria[]>('/cadastros/categorias.php?somente_ativos=1'));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const carregarOpcoesExistentes = useCallback(async () => {
    try {
      const [ativos, ambientes] = await Promise.all([
        api<AtivoExistente[]>('/ativos/ativos.php'),
        api<AmbienteModelo[]>('/cadastros/cliente_ambientes.php?modelos=1'),
      ]);
      setAtivosDisponiveis(ativos);
      setAmbientesDisponiveis(ambientes);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    const cid = clienteIdInicial !== undefined ? clienteIdInicial : (idParam ? Number(idParam) : null);
    setErro('');
    setMsg('');
    if (cid) {
      setClienteId(cid);
      carregarFicha(cid);
      return;
    }
    setClienteId(null);
    setIdentificacao(IDENTIFICACAO_VAZIA);
    setEndereco(ENDERECO_VAZIO);
    setAmbientes([]);
    setEquipamentos([]);
    setDocumentos([]);
    setNovoEquip(EQUIPAMENTO_VAZIO);
    setNovoAmbiente(AMBIENTE_VAZIO);
    setNovoDoc(DOCUMENTO_VAZIO);
    setArquivoDoc(null);
    setAmbienteInline('');
    setTipoChamadoInline('');
    setBuscaEquipamento('');
    setBuscaAmbiente('');
    setEditandoAmbiente(null);
  }, [clienteIdInicial, idParam, carregarFicha]);

  useEffect(() => {
    carregarCategorias();
  }, [carregarCategorias]);

  useEffect(() => {
    carregarOpcoesExistentes();
  }, [carregarOpcoesExistentes]);

  useEffect(() => {
    const cep = apenasDigitos(endereco.cep);
    if (cep.length !== 8 || cep === ultimoCepConsultado.current) return;
    ultimoCepConsultado.current = cep;

    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: ctrl.signal });
        if (!resp.ok) return;
        const dados = await resp.json() as ViaCepResposta;
        if (dados.erro) return;
        setEndereco((atual) => ({
          ...atual,
          cep: dados.cep || atual.cep,
          logradouro: dados.logradouro || atual.logradouro,
          complemento: atual.complemento || dados.complemento || '',
          bairro: dados.bairro || atual.bairro,
          cidade: dados.localidade || atual.cidade,
          uf: (dados.uf || atual.uf).toUpperCase(),
        }));
      } catch (e) {
        if ((e as Error).name !== 'AbortError') console.warn('ViaCEP indisponivel', e);
      }
    }, 350);

    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [endereco.cep]);

  // --------------------------------------------------------------- passo 1 e 2
  // Cada passo salva ao avançar (validar do <Passos>), então fechar a aba no meio
  // não joga fora o que já foi preenchido.
  async function salvarIdentificacao(): Promise<string | null> {
    const nome = identificacao.nome.trim();
    if (!nome) return null;
    setErro('');
    try {
      const corpo = {
        nome,
        documento: identificacao.documento.trim() || null,
        email: identificacao.email.trim() || null,
        telefone: identificacao.telefone.trim() || null,
        whatsapp: identificacao.whatsapp.trim() || null,
      };
      if (clienteId) {
        await api('/cadastros/clientes.php', { method: 'PUT', body: { id: clienteId, ...corpo } });
      } else {
        const r = await api<{ id: number }>('/cadastros/clientes.php', { method: 'POST', body: corpo });
        setClienteId(r.id);
        // Passa a ser edição: a URL acompanha, então recarregar a página não cria
        // um cliente duplicado.
      }
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }

  async function salvarEndereco(): Promise<string | null> {
    if (!clienteId) return null;
    try {
      await api('/cadastros/clientes.php', {
        method: 'PUT',
        body: {
          id: clienteId,
          cep: endereco.cep.trim() || null,
          logradouro: endereco.logradouro.trim() || null,
          numero: endereco.numero.trim() || null,
          complemento: endereco.complemento.trim() || null,
          bairro: endereco.bairro.trim() || null,
          cidade: endereco.cidade.trim() || null,
          uf: endereco.uf.trim().toUpperCase() || null,
        },
      });
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }

  // ----------------------------------------------------------------- documentos
  async function enviarDocumento() {
    if (!clienteId) { setErro('Salve o cliente primeiro.'); return; }
    if (!novoDoc.titulo.trim()) { setErro('Informe o título do documento.'); return; }
    if (!arquivoDoc) { setErro('Escolha o arquivo do documento.'); return; }
    setErro(''); setMsg(''); setOcupado(true);
    try {
      const fd = new FormData();
      fd.append('cliente_id', String(clienteId));
      fd.append('titulo', novoDoc.titulo.trim());
      fd.append('tipo', novoDoc.tipo);
      if (novoDoc.data_emissao) fd.append('data_emissao', novoDoc.data_emissao);
      if (novoDoc.data_validade) fd.append('data_validade', novoDoc.data_validade);
      fd.append('arquivo', arquivoDoc);
      const r = await api<{ documentos: Documento[] }>('/cadastros/cliente_documentos.php', { method: 'POST', body: fd, isForm: true });
      setDocumentos(r.documentos);
      setNovoDoc(DOCUMENTO_VAZIO);
      setArquivoDoc(null);
      setMsg('Documento salvo.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function excluirDocumento(docId: number) {
    setErro(''); setMsg(''); setOcupado(true);
    try {
      const r = await api<{ documentos: Documento[] }>('/cadastros/cliente_documentos.php?id=' + docId, { method: 'DELETE' });
      setDocumentos(r.documentos);
      setMsg('Documento excluído.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  // ---------------------------------------------------------------- equipamentos
  // "só ia clicando em adicionar": grava, limpa o formulário e mantém o ambiente
  // escolhido, porque normalmente vêm vários aparelhos do mesmo ambiente.
  async function criarTipoChamadoInline(): Promise<string> {
    const nome = tipoChamadoInline.trim();
    if (!nome) {
      setErro('Informe o nome do novo tipo de chamado.');
      return '';
    }
    const r = await api<{ id: number }>('/cadastros/categorias.php', {
      method: 'POST',
      body: { nome, tipo: 'chamado', status: 'ativo' },
    });
    await carregarCategorias();
    setTipoChamadoInline('');
    return String(r.id);
  }

  function escolherEquipamentoExistente(id: string) {
    setEquipamentoExistenteId(id);
    if (!id) return;
    const a = ativosDisponiveis.find((item) => item.id === Number(id));
    if (!a) return;
    setNovoEquip({
      nome: a.nome || '',
      capacidade_btu: a.capacidade_btu === null || a.capacidade_btu === undefined ? '' : String(a.capacidade_btu),
      cliente_ambiente_id: a.cliente_ambiente_id ? String(a.cliente_ambiente_id) : '',
      categoria_id: a.categoria_id ? String(a.categoria_id) : '',
      marca: a.marca || '',
      modelo: a.modelo || '',
      patrimonio: a.patrimonio || '',
      numero_serie: a.numero_serie || '',
    });
  }

  function escolherAmbienteModelo(id: string) {
    setAmbienteModeloId(id);
    if (!id) return;
    const a = ambientesDisponiveis.find((item) => item.id === Number(id));
    if (!a) return;
    setNovoAmbiente({
      nome: a.nome || '',
      area_m2: a.area_m2 === null ? '' : String(a.area_m2),
      ocupantes_max: a.ocupantes_max === null ? '' : String(a.ocupantes_max),
      atividade_ambiente: a.atividade_ambiente || '',
      observacao: a.observacao || '',
    });
  }

  async function adicionarEquipamento() {
    if (!clienteId) { setErro('Salve o cliente primeiro.'); return; }
    if (!equipamentoExistenteId && !novoEquip.nome.trim()) { setErro('Informe o nome do equipamento.'); return; }
    setErro(''); setMsg(''); setOcupado(true);
    try {
      let ambienteId = novoEquip.cliente_ambiente_id;
      let categoriaId = novoEquip.categoria_id;

      // "+ novo ambiente" digitado aqui: cria o ambiente antes, para o documento
      // poder manter Ambientes como último passo sem travar este.
      if (ambienteId === 'novo') {
        const nomeAmb = ambienteInline.trim();
        if (!nomeAmb) { setErro('Informe o nome do novo ambiente.'); setOcupado(false); return; }
        const r = await api<{ id: number; ambientes: Ambiente[] }>('/cadastros/cliente_ambientes.php', {
          method: 'POST',
          body: { cliente_id: clienteId, nome: nomeAmb },
        });
        setAmbientes(r.ambientes);
        ambienteId = String(r.id);
        setAmbienteInline('');
      }
      const ambienteModelo = ambienteId
        ? ambientesDisponiveis.find((a) => a.id === Number(ambienteId) && !ambientes.some((atual) => atual.id === a.id))
        : null;
      if (ambienteModelo) {
        const r = await api<{ id: number; ambientes: Ambiente[] }>('/cadastros/cliente_ambientes.php', {
          method: 'POST',
          body: {
            cliente_id: clienteId,
            nome: ambienteModelo.nome,
            area_m2: ambienteModelo.area_m2,
            ocupantes_max: ambienteModelo.ocupantes_max,
            atividade_ambiente: ambienteModelo.atividade_ambiente,
            observacao: ambienteModelo.observacao,
          },
        });
        setAmbientes(r.ambientes);
        ambienteId = String(r.id);
      }
      if (categoriaId === 'novo') {
        categoriaId = await criarTipoChamadoInline();
        if (!categoriaId) { setOcupado(false); return; }
      }

      const btu = novoEquip.capacidade_btu.replace(',', '.');
      const r = await api<{ equipamentos: Equipamento[] }>('/cadastros/cliente_equipamentos.php', {
        method: 'POST',
        body: {
          cliente_id: clienteId,
          ativo_id: equipamentoExistenteId ? Number(equipamentoExistenteId) : undefined,
          nome: novoEquip.nome.trim(),
          capacidade_btu: btu === '' ? null : Number(btu),
          cliente_ambiente_id: ambienteId ? Number(ambienteId) : null,
          categoria_id: categoriaId ? Number(categoriaId) : null,
          marca: novoEquip.marca.trim() || null,
          modelo: novoEquip.modelo.trim() || null,
          patrimonio: novoEquip.patrimonio.trim() || null,
          numero_serie: novoEquip.numero_serie.trim() || null,
        },
      });
      setEquipamentos(r.equipamentos);
      setEquipamentoExistenteId('');
      setNovoEquip({ ...EQUIPAMENTO_VAZIO, cliente_ambiente_id: ambienteId || '', categoria_id: categoriaId || '' });
      await carregarOpcoesExistentes();
      setMsg('Equipamento adicionado.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alternarStatusEquipamento(ativoId: number, statusAtual: string) {
    if (!clienteId) return;
    setErro(''); setMsg(''); setOcupado(true);
    const proximo = statusAtual === 'inativo' ? 'ativo' : 'inativo';
    try {
      const r = await api<{ equipamentos: Equipamento[] }>('/cadastros/cliente_equipamentos.php', {
        method: 'PUT',
        body: { id: ativoId, cliente_id: clienteId, status: proximo },
      });
      setEquipamentos(r.equipamentos);
      setMsg(proximo === 'ativo' ? 'Equipamento reativado.' : 'Equipamento desativado.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alterarEquipamento(ativoId: number, campo: 'capacidade_btu' | 'cliente_ambiente_id' | 'categoria_id', valor: string) {
    if (!clienteId) return;
    setErro(''); setMsg(''); setOcupado(true);
    try {
      const corpo: Record<string, unknown> = { id: ativoId, cliente_id: clienteId };
      if (campo === 'capacidade_btu') {
        const n = valor.replace(',', '.');
        corpo.capacidade_btu = n === '' ? null : Number(n);
      } else {
        corpo[campo] = valor ? Number(valor) : null;
      }
      const r = await api<{ equipamentos: Equipamento[] }>('/cadastros/cliente_equipamentos.php', { method: 'PUT', body: corpo });
      setEquipamentos(r.equipamentos);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  // ------------------------------------------------------------------- ambientes
  async function salvarAmbiente() {
    if (!clienteId) { setErro('Salve o cliente primeiro.'); return; }
    if (!novoAmbiente.nome.trim()) { setErro('Informe o nome do ambiente.'); return; }
    setErro(''); setMsg(''); setOcupado(true);
    try {
      const corpo = {
        nome: novoAmbiente.nome.trim(),
        area_m2: novoAmbiente.area_m2 === '' ? null : Number(novoAmbiente.area_m2.replace(',', '.')),
        ocupantes_max: novoAmbiente.ocupantes_max === '' ? null : Number(novoAmbiente.ocupantes_max),
        atividade_ambiente: novoAmbiente.atividade_ambiente.trim() || null,
        observacao: novoAmbiente.observacao.trim() || null,
      };
      const r = editandoAmbiente
        ? await api<{ ambientes: Ambiente[] }>('/cadastros/cliente_ambientes.php', { method: 'PUT', body: { id: editandoAmbiente, ...corpo } })
        : await api<{ ambientes: Ambiente[] }>('/cadastros/cliente_ambientes.php', { method: 'POST', body: { cliente_id: clienteId, ...corpo } });
      setAmbientes(r.ambientes);
      setNovoAmbiente(AMBIENTE_VAZIO);
      setAmbienteModeloId('');
      setEditandoAmbiente(null);
      await carregarOpcoesExistentes();
      setMsg(editandoAmbiente ? 'Ambiente atualizado.' : 'Ambiente adicionado.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alternarStatusAmbiente(ambienteId: number, statusAtual: string) {
    setErro(''); setMsg(''); setOcupado(true);
    const proximo = statusAtual === 'inativo' ? 'ativo' : 'inativo';
    try {
      const r = await api<{ ambientes: Ambiente[] }>('/cadastros/cliente_ambientes.php', {
        method: 'PUT',
        body: { id: ambienteId, status: proximo },
      });
      setAmbientes(r.ambientes);
      setMsg(proximo === 'ativo' ? 'Ambiente reativado.' : 'Ambiente desativado.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  function editarAmbiente(a: Ambiente) {
    setEditandoAmbiente(a.id);
    setNovoAmbiente({
      nome: a.nome,
      area_m2: a.area_m2 === null ? '' : String(a.area_m2),
      ocupantes_max: a.ocupantes_max === null ? '' : String(a.ocupantes_max),
      atividade_ambiente: a.atividade_ambiente || '',
      observacao: a.observacao || '',
    });
  }

  // O que o plano PMOC vai puxar, e as atividades sugeridas para o cronograma.
  // Falha aqui nao trava a ficha: os passos de PMOC ficam avisando o que falta.
  useEffect(() => {
    if (!clienteId) { setPreviaPmoc(null); return; }
    let vivo = true;
    api<PreviaPmoc>('/pmoc/plano_do_cliente.php?cliente_id=' + clienteId)
      .then((p) => {
        if (!vivo) return;
        setPreviaPmoc(p);
        setNomePlano((atual) => atual || 'PMOC - ' + (identificacao.nome || 'cliente'));
        // ADOTA o plano ativo que o cliente ja tem, em vez de criar outro.
        // Sem isso, cada vez que a ficha era reaberta e o passo Cronograma
        // avancado nascia um plano novo - foi assim que um cliente acumulou 7
        // planos, 6 deles vazios, e o PDF saiu com 7 folhas.
        const existente = p.planos_existentes.find((x) => x.status === 'ativo');
        if (existente) {
          setPlanoId((atual) => atual ?? existente.id);
          // Contadores do plano adotado, para o passo final nao mostrar 0.
          api<{ ambientes_qtd: number | string; ativos_qtd: number | string }>('/pmoc/planos.php?id=' + existente.id)
            .then((pl) => {
              if (!vivo) return;
              setResumoPuxado((atual) => atual ?? {
                ambientes: Number(pl.ambientes_qtd) || 0,
                ativos: Number(pl.ativos_qtd) || 0,
              });
            })
            .catch(() => {});
        }
        // Só semeia o cronograma na primeira carga: sobrescrever apagaria o que o
        // usuário já ajustou na tela.
        setCronograma((atual) => (atual.length ? atual : p.atividades_sugeridas.map((a) => ({
          marcada: true,
          nome: a.nome,
          periodicidade_dias: String(a.periodicidade_dias),
          norma_referencia: a.norma_referencia || '',
        }))));
      })
      .catch(() => { if (vivo) setPreviaPmoc(null); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // Gera o plano (o "só puxa": copia ambientes e equipamentos do cliente) e grava
  // o cronograma. É UMA ação: avançar o passo Cronograma faz as duas coisas, para
  // não existir um "gerar plano" separado que o usuário tenha que descobrir.
  async function gerarPlanoECronograma(): Promise<string | null> {
    if (!clienteId) return 'Salve o cadastro do cliente primeiro.';
    if (!equipamentos.filter((e) => (e.status || 'ativo') === 'ativo').length) {
      return 'Cadastre pelo menos um equipamento ativo no passo Equipamentos antes de gerar o PMOC.';
    }
    const escolhidas = cronograma.filter((c) => c.marcada && c.nome.trim());
    if (!escolhidas.length) return 'Marque ao menos uma atividade para o cronograma.';
    if (!dataInicioPmoc) return 'Informe a data de início do cronograma.';

    try {
      let id = planoId;
      if (!id) {
        const r = await api<{
          plano_id: number; ambientes_criados: number; ativos_criados: number;
          equipamentos_transferidos?: EquipamentoTransferido[];
        }>('/pmoc/plano_do_cliente.php', {
          method: 'POST',
          body: {
            cliente_id: clienteId,
            nome: nomePlano.trim() || null,
            data_inicio: dataInicioPmoc || null,
            data_validade: dataValidadePmoc || null,
          },
        });
        id = r.plano_id;
        setPlanoId(r.plano_id);
        setResumoPuxado({ ambientes: r.ambientes_criados, ativos: r.ativos_criados });
        setTransferidos(r.equipamentos_transferidos || []);
      }
      const c = await api<{ atividades: number; programacoes: number }>('/pmoc/plano_cronograma.php', {
        method: 'POST',
        body: {
          plano_id: id,
          data_inicio: dataInicioPmoc,
          atividades: escolhidas.map((x) => ({
            nome: x.nome.trim(),
            periodicidade_dias: Number(x.periodicidade_dias),
            norma_referencia: x.norma_referencia.trim() || null,
          })),
        },
      });
      setResumoCronograma({ atividades: c.atividades, programacoes: c.programacoes });
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }

  // O PDF nao pode sair por <a href>: o link nao carrega o header Authorization.
  async function imprimirPmoc() {
    if (!planoId) { setErro('Gere o plano antes de imprimir.'); return; }
    setErro('');
    setOcupado(true);
    const win = window.open('', '_blank'); // aberto no gesto do clique, senao o pop-up e bloqueado
    if (win) win.document.write('Gerando o PMOC para impressão...');
    try {
      const res = await fetch(`${API_BASE}/pmoc/relatorio_pdf.php?plano_id=${planoId}`, {
        headers: { Authorization: 'Bearer ' + getToken() },
      });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      if (win) {
        win.location.href = url;
        try {
          win.addEventListener('load', () => { try { win.print(); } catch { /* usuário imprime pelo menu */ } });
        } catch { /* navegador não deixa observar a aba */ }
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      setErro('Não foi possível abrir o PMOC para impressão. O plano foi salvo e pode ser impresso de novo aqui.');
    } finally {
      setOcupado(false);
    }
  }

  // MTTR/MTBF do cliente: o dashboard do PMOC filtra pela unidade que o cadastro
  // provisionou para ele. Falha aqui nao trava a ficha - o passo so nao mostra
  // numero. Sem .finally(): o target do build e es5.
  useEffect(() => {
    if (!unidadeCliente) { setIndicadores(null); return; }
    let vivo = true;
    setCarregandoInd(true);
    api<{ mttr_mtbf: MttrMtbf }>('/pmoc/dashboard.php?unidade_id=' + unidadeCliente)
      .then((d) => { if (vivo) { setIndicadores(d.mttr_mtbf); setCarregandoInd(false); } })
      .catch(() => { if (vivo) { setIndicadores(null); setCarregandoInd(false); } });
    return () => { vivo = false; };
  }, [unidadeCliente]);

  const tituloSecao = {
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase' as const,
    letterSpacing: '.08em',
    color: 'var(--texto-suave)',
    margin: '18px 2px 8px',
  };
  const ambientesAtivos = ambientes.filter((a) => a.status === 'ativo');
  const ambientesParaEquipamento = [
    ...ambientesAtivos.map((a) => ({ id: a.id, nome: a.nome, origem: '' })),
    ...ambientesDisponiveis
      .filter((a) => !ambientesAtivos.some((atual) => atual.id === a.id))
      .map((a) => ({ id: a.id, nome: [a.nome, a.cliente_nome].filter(Boolean).join(' - '), origem: 'modelo' })),
  ];
  const categoriasChamado = categorias.filter((c) => c.tipo === 'chamado' || c.tipo === 'ambos');
  const equipamentosJaVinculados = new Set(equipamentos.map((eq) => eq.id));
  const ativosParaVincular = ativosDisponiveis.filter((a) => !equipamentosJaVinculados.has(a.id) || String(a.id) === equipamentoExistenteId);
  const termoEquipamento = buscaEquipamento.trim().toLowerCase();
  const equipamentosFiltrados = termoEquipamento
    ? equipamentos.filter((eq) => [
        eq.nome,
        eq.patrimonio,
        eq.marca,
        eq.modelo,
        eq.numero_serie,
        eq.ambiente_nome,
        eq.categoria_nome,
      ].some((v) => (v || '').toLowerCase().includes(termoEquipamento)))
    : equipamentos;
  const termoAmbiente = buscaAmbiente.trim().toLowerCase();
  const ambientesFiltrados = termoAmbiente
    ? ambientes.filter((a) => [
        a.nome,
        a.atividade_ambiente,
        a.observacao,
        a.area_m2 === null ? '' : String(a.area_m2),
        a.ocupantes_max === null ? '' : String(a.ocupantes_max),
      ].some((v) => (v || '').toLowerCase().includes(termoAmbiente)))
    : ambientes;
  const semCliente = !clienteId;

  const avisoSalveCliente = (
    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
      Preencha e avance o passo <strong>Nome</strong> primeiro: documentos, equipamentos e
      ambientes são pendurados no cliente, então ele precisa estar salvo.
    </p>
  );

  const passos: Passo[] = [
    {
      titulo: 'Nome',
      validar: salvarIdentificacao,
      node: (
        <div className="card">
          <div className="grid cols2">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Nome / Razão social</label>
              <input className="input" value={identificacao.nome} onChange={(e) => setIdentificacao({ ...identificacao, nome: e.target.value })} />
            </div>
            <div>
              <label>CPF / CNPJ</label>
              <input className="input" value={identificacao.documento} onChange={(e) => setIdentificacao({ ...identificacao, documento: e.target.value })} />
            </div>
            <div>
              <label>E-mail</label>
              <input className="input" value={identificacao.email} onChange={(e) => setIdentificacao({ ...identificacao, email: e.target.value })} />
            </div>
            <div>
              <label>Telefone</label>
              <input className="input" value={identificacao.telefone} onChange={(e) => setIdentificacao({ ...identificacao, telefone: e.target.value })} />
            </div>
            <div>
              <label>WhatsApp</label>
              <input className="input" value={identificacao.whatsapp} onChange={(e) => setIdentificacao({ ...identificacao, whatsapp: e.target.value })} placeholder="(11) 99999-9999" />
            </div>
          </div>
          <p className="muted" style={{ margin: '10px 0 0', fontSize: 12 }}>
            Ao avançar, o cliente é salvo e os próximos passos já gravam direto na ficha dele.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Endereço',
      validar: salvarEndereco,
      node: (
        <div className="card">
          <div className="grid cols2">
            <div>
              <label>CEP</label>
              <input className="input" value={endereco.cep} onChange={(e) => setEndereco({ ...endereco, cep: e.target.value })} placeholder="00000-000" />
            </div>
            <div>
              <label>Número</label>
              <input className="input" value={endereco.numero} onChange={(e) => setEndereco({ ...endereco, numero: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Logradouro</label>
              <input className="input" value={endereco.logradouro} onChange={(e) => setEndereco({ ...endereco, logradouro: e.target.value })} />
            </div>
            <div>
              <label>Complemento</label>
              <input className="input" value={endereco.complemento} onChange={(e) => setEndereco({ ...endereco, complemento: e.target.value })} />
            </div>
            <div>
              <label>Bairro</label>
              <input className="input" value={endereco.bairro} onChange={(e) => setEndereco({ ...endereco, bairro: e.target.value })} />
            </div>
            <div>
              <label>Cidade</label>
              <input className="input" value={endereco.cidade} onChange={(e) => setEndereco({ ...endereco, cidade: e.target.value })} />
            </div>
            <div>
              <label>UF</label>
              <input className="input" maxLength={2} value={endereco.uf} onChange={(e) => setEndereco({ ...endereco, uf: e.target.value.toUpperCase() })} placeholder="SP" />
            </div>
          </div>
          <p className="muted" style={{ margin: '10px 0 0', fontSize: 12 }}>
            O endereço vai para o plano PMOC deste cliente quando ele for gerado.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Documentos',
      node: (
        <div className="card">
          {semCliente ? avisoSalveCliente : (
            <>
              <div className="grid cols2" style={{ gap: 8, alignItems: 'end' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Título</label>
                  <input className="input" value={novoDoc.titulo} disabled={ocupado} onChange={(e) => setNovoDoc({ ...novoDoc, titulo: e.target.value })} placeholder="Ex.: Contrato de manutenção 2026" />
                </div>
                <div>
                  <label>Tipo</label>
                  <select value={novoDoc.tipo} disabled={ocupado} onChange={(e) => setNovoDoc({ ...novoDoc, tipo: e.target.value })}>
                    {tiposDocumento.map((t) => <option key={t} value={t}>{ROTULO_TIPO_DOC[t] || t}</option>)}
                  </select>
                </div>
                <div>
                  <label>Arquivo (PDF ou imagem, até 12 MB)</label>
                  <input
                    className="input"
                    type="file"
                    accept="application/pdf,image/*"
                    disabled={ocupado}
                    onChange={(e) => setArquivoDoc(e.currentTarget.files?.[0] || null)}
                  />
                </div>
                <div>
                  <label>Emissão</label>
                  <input className="input" type="date" value={novoDoc.data_emissao} disabled={ocupado} onChange={(e) => setNovoDoc({ ...novoDoc, data_emissao: e.target.value })} />
                </div>
                <div>
                  <label>Validade</label>
                  <input className="input" type="date" value={novoDoc.data_validade} disabled={ocupado} onChange={(e) => setNovoDoc({ ...novoDoc, data_validade: e.target.value })} />
                </div>
                <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <button type="button" className="btn" disabled={ocupado} onClick={enviarDocumento}>
                    {ocupado ? 'Enviando...' : 'Adicionar documento'}
                  </button>
                </div>
              </div>

              <div className="tabela-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr><th>Documento</th><th>Tipo</th><th>Emissão</th><th>Validade</th><th /></tr>
                  </thead>
                  <tbody>
                    {documentos.map((d) => (
                      <tr key={d.id}>
                        <td><a href={urlImagem(d.arquivo_url)} target="_blank" rel="noreferrer">{d.titulo}</a></td>
                        <td>{ROTULO_TIPO_DOC[d.tipo] || d.tipo}</td>
                        <td>{dataTexto(d.data_emissao)}</td>
                        <td>{dataTexto(d.data_validade)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button type="button" className="btn danger sm" disabled={ocupado} onClick={() => excluirDocumento(d.id)}>Excluir</button>
                        </td>
                      </tr>
                    ))}
                    {!documentos.length && <tr><td colSpan={5} className="vazio">Nenhum documento anexado.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      titulo: 'Equipamentos',
      node: (
        <div className="card">
          {semCliente ? avisoSalveCliente : (
            <>
              <div className="grid cols2" style={{ gap: 8, alignItems: 'end' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Equipamento já cadastrado</label>
                  <select
                    value={equipamentoExistenteId}
                    disabled={ocupado}
                    onChange={(e) => escolherEquipamentoExistente(e.target.value)}
                  >
                    <option value="">Cadastrar novo equipamento</option>
                    {ativosParaVincular.map((a) => (
                      <option key={a.id} value={a.id}>
                        {[a.nome, a.patrimonio, a.marca, a.modelo].filter(Boolean).join(' - ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Equipamento</label>
                  <input
                    className="input"
                    value={novoEquip.nome}
                    disabled={ocupado}
                    onChange={(e) => setNovoEquip({ ...novoEquip, nome: e.target.value })}
                    placeholder="Ex.: Split Hi-Wall sala de reunião"
                  />
                </div>
                <div>
                  <label>Capacidade (BTU)</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="1"
                    value={novoEquip.capacidade_btu}
                    disabled={ocupado}
                    onChange={(e) => setNovoEquip({ ...novoEquip, capacidade_btu: e.target.value })}
                    placeholder="12000"
                  />
                </div>
                <div>
                  <label>Tipo de chamado</label>
                  <select
                    value={novoEquip.categoria_id}
                    disabled={ocupado}
                    onChange={(e) => setNovoEquip({ ...novoEquip, categoria_id: e.target.value })}
                  >
                    <option value="">Sem tipo</option>
                    {categoriasChamado.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    <option value="novo">+ novo tipo de chamado</option>
                  </select>
                </div>
                {novoEquip.categoria_id === 'novo' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Nome do novo tipo de chamado</label>
                    <input
                      className="input"
                      value={tipoChamadoInline}
                      disabled={ocupado}
                      onChange={(e) => setTipoChamadoInline(e.target.value)}
                      placeholder="Ex.: Preventiva"
                    />
                  </div>
                )}
                <div>
                  <label>Ambiente</label>
                  <select
                    value={novoEquip.cliente_ambiente_id}
                    disabled={ocupado}
                    onChange={(e) => setNovoEquip({ ...novoEquip, cliente_ambiente_id: e.target.value })}
                  >
                    <option value="">Sem ambiente</option>
                    {ambientesParaEquipamento.map((a) => <option key={`${a.origem}-${a.id}`} value={a.id}>{a.nome}</option>)}
                    <option value="novo">+ novo ambiente</option>
                  </select>
                </div>
                {novoEquip.cliente_ambiente_id === 'novo' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Nome do novo ambiente</label>
                    <input
                      className="input"
                      value={ambienteInline}
                      disabled={ocupado}
                      onChange={(e) => setAmbienteInline(e.target.value)}
                      placeholder="Ex.: Recepção"
                    />
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                      Área, ocupantes e atividade você completa no passo Ambientes.
                    </p>
                  </div>
                )}
                <div>
                  <label>Marca</label>
                  <input className="input" value={novoEquip.marca} disabled={ocupado} onChange={(e) => setNovoEquip({ ...novoEquip, marca: e.target.value })} />
                </div>
                <div>
                  <label>Modelo</label>
                  <input className="input" value={novoEquip.modelo} disabled={ocupado} onChange={(e) => setNovoEquip({ ...novoEquip, modelo: e.target.value })} />
                </div>
                <div>
                  <label>Patrimônio</label>
                  <input className="input" value={novoEquip.patrimonio} disabled={ocupado} onChange={(e) => setNovoEquip({ ...novoEquip, patrimonio: e.target.value })} />
                </div>
                <div>
                  <label>{rotuloSerie}</label>
                  <input className="input" value={novoEquip.numero_serie} disabled={ocupado} onChange={(e) => setNovoEquip({ ...novoEquip, numero_serie: e.target.value })} />
                </div>
                <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <button type="button" className="btn" disabled={ocupado} onClick={adicionarEquipamento}>
                    {ocupado ? 'Salvando...' : '+ Adicionar equipamento'}
                  </button>
                </div>
              </div>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                O ambiente escolhido continua selecionado depois de adicionar — dá para lançar
                vários aparelhos do mesmo ambiente em sequência.
              </p>

              <div style={{ marginTop: 12 }}>
                <label>Pesquisar equipamento</label>
                <input
                  className="input"
                  value={buscaEquipamento}
                  disabled={ocupado}
                  onChange={(e) => setBuscaEquipamento(e.target.value)}
                  placeholder="Buscar por equipamento, tipo, ambiente, marca, modelo, patrimonio ou serie"
                />
              </div>

              <div className="tabela-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr><th>Equipamento</th><th style={{ width: 130 }}>BTU</th><th style={{ width: 180 }}>Tipo</th><th style={{ width: 180 }}>Ambiente</th><th>Marca / Modelo</th><th /></tr>
                  </thead>
                  <tbody>
                    {equipamentosFiltrados.map((eq) => (
                      <tr key={eq.id} style={eq.status === 'inativo' ? { opacity: 0.55 } : undefined}>
                        <td>
                          {eq.nome}
                          {eq.patrimonio ? <span className="muted"> · {eq.patrimonio}</span> : null}
                        </td>
                        <td>
                          <input
                            className="input"
                            type="number"
                            min="0"
                            step="1"
                            defaultValue={eq.capacidade_btu === null ? '' : eq.capacidade_btu}
                            disabled={ocupado}
                            onBlur={(e) => {
                              const atual = eq.capacidade_btu === null ? '' : String(eq.capacidade_btu);
                              if (e.target.value !== atual) alterarEquipamento(eq.id, 'capacidade_btu', e.target.value);
                            }}
                            style={{ width: 110 }}
                          />
                        </td>
                        <td>
                          <select
                            value={eq.categoria_id === null ? '' : String(eq.categoria_id)}
                            disabled={ocupado}
                            onChange={(e) => alterarEquipamento(eq.id, 'categoria_id', e.target.value)}
                          >
                            <option value="">Sem tipo</option>
                            {categoriasChamado.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                          </select>
                        </td>
                        <td>
                          <select
                            value={eq.cliente_ambiente_id === null ? '' : String(eq.cliente_ambiente_id)}
                            disabled={ocupado}
                            onChange={(e) => alterarEquipamento(eq.id, 'cliente_ambiente_id', e.target.value)}
                          >
                            <option value="">Sem ambiente</option>
                            {ambientesAtivos.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
                          </select>
                        </td>
                        <td>
                          {[eq.marca, eq.modelo].filter(Boolean).join(' / ') || '—'}
                          {eq.numero_serie ? <div className="muted">{rotuloSerie}: {eq.numero_serie}</div> : null}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className={`btn ${eq.status === 'inativo' ? 'sec' : 'danger'} sm`}
                            disabled={ocupado}
                            onClick={() => alternarStatusEquipamento(eq.id, eq.status)}
                          >
                            {eq.status === 'inativo' ? 'Ativar' : 'Desativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!equipamentosFiltrados.length && <tr><td colSpan={6} className="vazio">{equipamentos.length ? 'Nenhum equipamento encontrado.' : 'Nenhum equipamento cadastrado para este cliente.'}</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
                Desativar mantem o equipamento vinculado ao cliente, mas tira ele das novas selecoes.
              </p>
            </>
          )}
        </div>
      ),
    },
    {
      titulo: 'Ambientes',
      node: (
        <div className="card">
          {semCliente ? avisoSalveCliente : (
            <>
              <div className="grid cols2" style={{ gap: 8, alignItems: 'end' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Ambiente já cadastrado</label>
                  <select
                    value={ambienteModeloId}
                    disabled={ocupado}
                    onChange={(e) => escolherAmbienteModelo(e.target.value)}
                  >
                    <option value="">Cadastrar novo ambiente</option>
                    {ambientesDisponiveis.map((a) => (
                      <option key={a.id} value={a.id}>
                        {[a.nome, a.cliente_nome].filter(Boolean).join(' - ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Ambiente</label>
                  <input className="input" value={novoAmbiente.nome} disabled={ocupado} onChange={(e) => setNovoAmbiente({ ...novoAmbiente, nome: e.target.value })} placeholder="Ex.: Sala de reunião" />
                </div>
                <div>
                  <label>Área (m²)</label>
                  <input className="input" type="number" min="0" step="0.01" value={novoAmbiente.area_m2} disabled={ocupado} onChange={(e) => setNovoAmbiente({ ...novoAmbiente, area_m2: e.target.value })} />
                </div>
                <div>
                  <label>Ocupantes (máx.)</label>
                  <input className="input" type="number" min="0" step="1" value={novoAmbiente.ocupantes_max} disabled={ocupado} onChange={(e) => setNovoAmbiente({ ...novoAmbiente, ocupantes_max: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Atividade do ambiente</label>
                  <input className="input" value={novoAmbiente.atividade_ambiente} disabled={ocupado} onChange={(e) => setNovoAmbiente({ ...novoAmbiente, atividade_ambiente: e.target.value })} placeholder="Ex.: Escritório, atendimento ao público" />
                </div>
                <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  {editandoAmbiente && (
                    <button type="button" className="btn sec" disabled={ocupado} onClick={() => { setEditandoAmbiente(null); setNovoAmbiente(AMBIENTE_VAZIO); }}>
                      Cancelar edição
                    </button>
                  )}
                  <button type="button" className="btn" disabled={ocupado} onClick={salvarAmbiente}>
                    {ocupado ? 'Salvando...' : editandoAmbiente ? 'Salvar ambiente' : '+ Adicionar ambiente'}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label>Pesquisar ambiente</label>
                <input
                  className="input"
                  value={buscaAmbiente}
                  disabled={ocupado}
                  onChange={(e) => setBuscaAmbiente(e.target.value)}
                  placeholder="Buscar por ambiente, atividade, area ou ocupantes"
                />
              </div>

              <div className="tabela-wrap" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr><th>Ambiente</th><th>Área</th><th>Ocupantes</th><th>Atividade</th><th>Equip.</th><th /></tr>
                  </thead>
                  <tbody>
                    {ambientesFiltrados.map((a) => (
                      <tr key={a.id} style={a.status === 'inativo' ? { opacity: 0.55 } : undefined}>
                        <td>{a.nome}{a.status === 'inativo' ? <span className="muted"> · inativo</span> : null}</td>
                        <td>{a.area_m2 === null ? '—' : a.area_m2.toLocaleString('pt-BR') + ' m²'}</td>
                        <td>{a.ocupantes_max === null ? '—' : a.ocupantes_max}</td>
                        <td>{a.atividade_ambiente || '—'}</td>
                        <td>{a.equipamentos}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button type="button" className="btn sec sm" disabled={ocupado} onClick={() => editarAmbiente(a)}>Editar</button>
                          <button
                            type="button"
                            className={`btn ${a.status === 'inativo' ? 'sec' : 'danger'} sm`}
                            style={{ marginLeft: 6 }}
                            disabled={ocupado}
                            onClick={() => alternarStatusAmbiente(a.id, a.status)}
                          >
                            {a.status === 'inativo' ? 'Ativar' : 'Desativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!ambientesFiltrados.length && <tr><td colSpan={6} className="vazio">{ambientes.length ? 'Nenhum ambiente encontrado.' : 'Nenhum ambiente cadastrado.'}</td></tr>}
                  </tbody>
                </table>
              </div>

              <div style={tituloSecao}>Pronto</div>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Com ambientes e equipamentos cadastrados aqui, o plano PMOC deste cliente já
                puxa tudo — não precisa cadastrar de novo.
              </p>
              <div className="form-actions" style={{ marginTop: 12 }}>
                <button type="button" className="btn sec" onClick={() => onVoltar ? onVoltar() : nav('/app/cadastros')}>Voltar para Cadastros</button>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      titulo: 'Cronograma',
      // Avancar aqui faz as duas coisas: cria o plano (puxando ambientes e
      // equipamentos deste cliente) e grava o cronograma. Um passo, uma acao.
      validar: gerarPlanoECronograma,
      node: (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Cronograma do PMOC</h3>
          {!clienteId ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Salve o cadastro do cliente primeiro.</p>
          ) : (
            <>
              <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
                As atividades já vêm sugeridas — só ajuste o que precisar. Ao avançar, o plano PMOC é gerado
                puxando os <strong>equipamentos e ambientes desta ficha</strong>, e o cronograma é gravado.
              </p>

              {!!planoId ? (
                <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
                  Este cliente já tem plano PMOC. Avançar <strong>atualiza o cronograma dele</strong> — não cria
                  outro plano.
                </p>
              ) : (
                <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
                  Avançar gera o plano PMOC deste cliente, puxando os equipamentos e ambientes cadastrados acima.
                </p>
              )}
              {!!previaPmoc && previaPmoc.equipamentos_disponiveis < previaPmoc.equipamentos.length && (
                <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
                  {previaPmoc.equipamentos.length - previaPmoc.equipamentos_disponiveis} equipamento(s) estão em
                  outro plano PMOC e serão <strong>transferidos</strong> para este — um equipamento pertence a um
                  plano por vez, então o plano anterior deixa de cobri-lo.
                </p>
              )}

              <div className="grid cols2">
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Nome do plano</label>
                  <input className="input" value={nomePlano} disabled={ocupado} onChange={(e) => setNomePlano(e.target.value)} placeholder="Ex.: PMOC 2026" />
                </div>
                <div>
                  <label>Início *</label>
                  <input className="input" type="date" value={dataInicioPmoc} disabled={ocupado} onChange={(e) => setDataInicioPmoc(e.target.value)} />
                </div>
                <div>
                  <label>Validade</label>
                  <input className="input" type="date" value={dataValidadePmoc} disabled={ocupado} onChange={(e) => setDataValidadePmoc(e.target.value)} />
                </div>
              </div>

              <div style={tituloSecao}>Atividades</div>
              <div className="tabela-wrap">
                <table>
                  <thead><tr><th style={{ width: 40 }} /><th>Atividade</th><th style={{ width: 150 }}>A cada (dias)</th><th>Norma</th></tr></thead>
                  <tbody>
                    {cronograma.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            type="checkbox"
                            checked={c.marcada}
                            onChange={(e) => setCronograma((a) => a.map((x, k) => (k === i ? { ...x, marcada: e.target.checked } : x)))}
                          />
                        </td>
                        <td>
                          <input
                            className="input"
                            value={c.nome}
                            onChange={(e) => setCronograma((a) => a.map((x, k) => (k === i ? { ...x, nome: e.target.value } : x)))}
                          />
                        </td>
                        <td>
                          <input
                            className="input"
                            type="number"
                            min="1"
                            max="3650"
                            value={c.periodicidade_dias}
                            onChange={(e) => setCronograma((a) => a.map((x, k) => (k === i ? { ...x, periodicidade_dias: e.target.value } : x)))}
                            style={{ width: 110 }}
                          />
                        </td>
                        <td>
                          <input
                            className="input"
                            value={c.norma_referencia}
                            onChange={(e) => setCronograma((a) => a.map((x, k) => (k === i ? { ...x, norma_referencia: e.target.value } : x)))}
                          />
                        </td>
                      </tr>
                    ))}
                    {!cronograma.length && <tr><td colSpan={4} className="vazio">Nenhuma atividade. Adicione abaixo.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="form-actions" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn sec sm"
                  onClick={() => setCronograma((a) => [...a, { marcada: true, nome: '', periodicidade_dias: '30', norma_referencia: '' }])}
                >
                  + Outra atividade
                </button>
              </div>
              {resumoCronograma && (
                <p className="muted" style={{ margin: '10px 0 0', fontSize: 12 }}>
                  Cronograma atual: {resumoCronograma.atividades} atividade(s), {resumoCronograma.programacoes} programação(ões).
                  Avançar de novo substitui este cronograma.
                </p>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      titulo: 'MTTR/MTBF',
      node: (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Indicadores de manutenção</h3>
          {/* CONFERENCIA, nao preenchimento: MTTR e MTBF sao calculados a partir das
              falhas registradas nas OS deste cliente. Cliente novo nao tem historico,
              e a tela diz isso em vez de mostrar zero como se fosse resultado. */}
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            MTTR e MTBF são <strong>calculados</strong> automaticamente: toda OS concluída que tem equipamento
            gera o evento sozinha. OS <strong>preventiva</strong> (ou gerada pelo cronograma do PMOC) é registrada
            como <strong>parada planejada</strong> e não conta como falha — senão cada limpeza de rotina viraria
            uma quebra e o equipamento pareceria pior quanto melhor fosse mantido.
          </p>

          {!clienteId ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Salve o cadastro para ver os indicadores.</p>
          ) : !unidadeCliente ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Este cliente ainda não tem histórico de manutenção. Os indicadores passam a ser calculados
              depois que houver OS com falha registrada.
            </p>
          ) : carregandoInd ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Calculando...</p>
          ) : !indicadores || !indicadores.eventos_falha ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Nenhuma falha registrada até agora, então não há MTTR nem MTBF para calcular. Não é zero — é
              ausência de histórico.
              {!!indicadores?.paradas_planejadas && (
                <> Há {indicadores.paradas_planejadas} parada(s) planejada(s) registrada(s), que de propósito
                não entram no cálculo.</>
              )}
            </p>
          ) : (
            <>
              <div className="os-dados">
                <div className="os-campo"><span className="k">Falhas registradas</span><span className="v">{indicadores.eventos_falha}</span></div>
                <div className="os-campo">
                  <span className="k">Paradas planejadas</span>
                  <span className="v">{indicadores.paradas_planejadas ?? 0} <small>(não contam)</small></span>
                </div>
                <div className="os-campo"><span className="k">MTTR técnico</span><span className="v">{indicadores.mttr_tecnico_hhmm || '—'}</span></div>
                <div className="os-campo"><span className="k">MTTR operacional</span><span className="v">{indicadores.mttr_operacional_hhmm || '—'}</span></div>
                <div className="os-campo">
                  <span className="k">MTBF</span>
                  <span className="v">
                    {indicadores.mtbf_hhmm || <>— <small>(precisa de 2 falhas no mesmo equipamento)</small></>}
                  </span>
                </div>
                <div className="os-campo">
                  <span className="k">Disponibilidade</span>
                  <span className="v">{indicadores.disponibilidade_percentual !== null ? indicadores.disponibilidade_percentual + '%' : '—'}</span>
                </div>
              </div>

              {!!indicadores.top_mttr?.length && (
                <>
                  <div style={tituloSecao}>Maior tempo de reparo (MTTR)</div>
                  <div className="tabela-wrap">
                    <table>
                      <thead><tr><th>Equipamento</th><th className="num">Falhas</th><th className="num">MTTR (min)</th></tr></thead>
                      <tbody>
                        {indicadores.top_mttr.map((t) => (
                          <tr key={t.ativo_id}>
                            <td>{t.ativo_nome}</td>
                            <td className="num">{t.falhas}</td>
                            <td className="num">{t.mttr_operacional_minutos ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!!indicadores.top_menor_mtbf?.length && (
                <>
                  <div style={tituloSecao}>Falha com mais frequência (menor MTBF)</div>
                  <div className="tabela-wrap">
                    <table>
                      <thead><tr><th>Equipamento</th><th className="num">Falhas</th><th className="num">MTBF (min)</th></tr></thead>
                      <tbody>
                        {indicadores.top_menor_mtbf.map((t) => (
                          <tr key={t.ativo_id}>
                            <td>{t.ativo_nome}</td>
                            <td className="num">{t.falhas}</td>
                            <td className="num">{t.mtbf_minutos ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      titulo: 'Gerar e imprimir',
      node: (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Plano PMOC</h3>
          <div className="os-dados">
            <div className="os-campo"><span className="k">Cliente</span><span className="v">{identificacao.nome || '—'}</span></div>
            <div className="os-campo"><span className="k">Plano</span><span className="v">{nomePlano || '—'}</span></div>
            <div className="os-campo">
              <span className="k">Vigência</span>
              <span className="v">
                {(dataInicioPmoc ? dataInicioPmoc.split('-').reverse().join('/') : '—')
                  + (dataValidadePmoc ? ' até ' + dataValidadePmoc.split('-').reverse().join('/') : '')}
              </span>
            </div>
            <div className="os-campo"><span className="k">Ambientes puxados</span><span className="v">{resumoPuxado?.ambientes ?? 0}</span></div>
            <div className="os-campo"><span className="k">Equipamentos puxados</span><span className="v">{resumoPuxado?.ativos ?? 0}</span></div>
            <div className="os-campo">
              <span className="k">Cronograma</span>
              <span className="v">
                {resumoCronograma
                  ? resumoCronograma.atividades + ' atividade(s) · ' + resumoCronograma.programacoes + ' programação(ões)'
                  : 'não gerado'}
              </span>
            </div>
          </div>

          {!!transferidos.length && (
            <p className="muted" style={{ margin: '10px 0 0', fontSize: 12 }}>
              {transferidos.length} equipamento(s) foram transferidos de outro plano para este:{' '}
              {transferidos.map((t) => t.nome + ' (de ' + (t.de_plano_nome || 'um plano já removido') + ')').join('; ')}.
              O cronograma que eles tinham no plano anterior foi encerrado.
            </p>
          )}

          {!planoId ? (
            <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
              O plano é gerado ao avançar o passo <strong>Cronograma</strong>. Volte um passo e avance.
            </p>
          ) : (
            <>
              <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>
                O plano já está salvo. O PMOC abre numa aba nova já pronto para imprimir.
              </p>
              <div className="form-actions" style={{ marginTop: 18 }}>
                <button type="button" className="btn sec" onClick={() => onVoltar ? onVoltar() : nav('/app/cadastros')}>
                  Voltar para Cadastros
                </button>
                <button type="button" className="btn" disabled={ocupado} onClick={imprimirPmoc}>
                  {ocupado ? 'Abrindo...' : 'Imprimir PMOC'}
                </button>
              </div>
            </>
          )}
        </div>
      ),
    },
  ]
    // "RETIRAR TELA PMOC": os tres passos de PMOC saem da ficha. A API do
    // PMOC tambem barra, entao deixa-los na tela daria erro 403 ao avancar.
    .filter((passo) => !isSemPmoc
      || !['Cronograma', 'MTTR/MTBF', 'Gerar e imprimir'].includes(passo.titulo));

  return (
    <div>
      <div className="titulo-pg">
        <h1>{clienteId ? 'Cadastro do cliente' : 'Novo cliente'}</h1>
      </div>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
        Nome → Endereço → Documentos → Equipamentos → Ambientes → Cronograma → MTTR/MTBF →
        Gerar e imprimir. Tudo na mesma tela.
      </p>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}
      <Passos passos={passos} rotuloFim="Cadastro completo" />
    </div>
  );
}
